import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import BrightnessAutoRoundedIcon from '@mui/icons-material/BrightnessAutoRounded';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import { useThemeMode } from '../theme/ThemeModeProvider';
import type { WeatherView } from '../weather/format';
import type { ConnectionStatus } from '../weather/types';

const THEME_LABEL: Record<string, string> = {
  auto: 'Temă automată (după sistem)',
  light: 'Temă de zi',
  dark: 'Temă de noapte',
};

const CONNECTION_LABEL: Record<ConnectionStatus, string | null> = {
  idle: null,
  connecting: 'Se conectează…',
  connected: null,
  reconnecting: 'Reconectare…',
  error: 'Deconectat',
};

interface AppHeaderProps {
  view: WeatherView;
  connection: ConnectionStatus;
}

export function AppHeader({ view, connection }: AppHeaderProps) {
  const { preference, mode, cyclePreference } = useThemeMode();

  const connectionLabel = CONNECTION_LABEL[connection];
  const degraded = connectionLabel !== null || view.stale;
  const ThemeIcon =
    preference === 'auto'
      ? BrightnessAutoRoundedIcon
      : mode === 'dark'
        ? DarkModeRoundedIcon
        : LightModeRoundedIcon;

  return (
    <Box
      component="header"
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '18px',
        padding: '6px 4px 24px',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <Box
          aria-hidden
          sx={(theme) => ({
            width: 46,
            height: 46,
            borderRadius: '16px',
            backgroundColor: theme.palette.weather.accentSoft,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            transition: 'background-color .5s ease',
          })}
        >
          <Box
            component="span"
            sx={(theme) => ({
              width: 9,
              height: 9,
              borderRadius: '50%',
              backgroundColor: theme.palette.weather.accent,
              display: 'block',
            })}
          />
          <Box
            component="span"
            sx={(theme) => ({
              position: 'absolute',
              width: 9,
              height: 9,
              borderRadius: '50%',
              backgroundColor: theme.palette.weather.accent,
              animation: 'muiPulse 2.4s ease-out infinite',
              '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 0 },
            })}
          />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
          <Typography
            variant="h1"
            sx={{ fontSize: 'clamp(21px, 2.6vw, 27px)', fontWeight: 600, margin: 0 }}
          >
            {view.stationName}
          </Typography>
          <Typography
            sx={(theme) => ({ fontSize: 14, color: theme.palette.weather.soft })}
          >
            {view.hasCondition && `${view.condition} · `}actualizat {view.lastSeenAgo}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: '2px',
            pr: '6px',
          }}
        >
          <Typography sx={{ fontSize: 19, fontWeight: 500 }}>{view.clock}</Typography>
          <Typography sx={(theme) => ({ fontSize: 13, color: theme.palette.weather.soft })}>
            {view.dateLabel}
          </Typography>
        </Box>

        <Box
          sx={(theme) => ({
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            height: 34,
            px: '14px',
            borderRadius: `${theme.radii.pill}px`,
            backgroundColor: theme.palette.weather.surface,
            boxShadow: theme.softShadows.card,
            fontSize: 13,
            fontWeight: 500,
            color: theme.palette.weather.soft,
          })}
        >
          <Box
            component="span"
            sx={(theme) => ({
              width: 7,
              height: 7,
              borderRadius: '50%',
              display: 'block',
              backgroundColor: degraded ? theme.palette.weather.accent : theme.palette.weather.ok,
            })}
          />
          <span>{connectionLabel ?? view.lastSeenTime}</span>
        </Box>

        <Tooltip title={THEME_LABEL[preference]}>
          <IconButton
            onClick={cyclePreference}
            aria-label={`${THEME_LABEL[preference]}. Comută tema.`}
            sx={(theme) => ({
              width: 42,
              height: 42,
              borderRadius: `${theme.radii.pill}px`,
              backgroundColor: theme.palette.weather.surface,
              boxShadow: theme.softShadows.card,
              color: theme.palette.weather.ink,
              transition: 'box-shadow .2s ease, transform .2s ease',
              '&:hover': {
                backgroundColor: theme.palette.weather.surface,
                boxShadow: theme.softShadows.cardRaised,
                transform: 'translateY(-1px)',
              },
              '&:active': { transform: 'translateY(0)' },
            })}
          >
            <ThemeIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
